import { equipmentDefinition } from "@frontier/content/arsenal";
import { fittingCells } from "@frontier/content/ship-fitting";
import type { EquipmentKey } from "@frontier/content/arsenal";
import type { Profile } from "@frontier/protocol/contracts";
import type { EquipmentContext } from "./equipment-selection";
import { EquipmentArt, equipmentBackdrop } from "./EquipmentArt";
import { sameEquipmentContext } from "./equipment-selection";
import { moduleLabels, weaponLabels } from "./ui-labels";
import { passiveInfo } from "./flight-labels";
import "./equipment-summary.css";
import { abilityHotkey } from "./ability-controls";

export function EquipmentSummary({
  profile,
  onOpen,
  context,
  busy = false,
}: {
  profile: Profile;
  onOpen: (context: EquipmentContext) => void;
  context?: EquipmentContext;
  busy?: boolean;
}) {
  let slots: {
    label: string;
    caption: string;
    name: string;
    grade: string | null;
    hotkey: string;
    theme: string;
    equipment: EquipmentKey | null;
    context: EquipmentContext;
  }[] = [
    {
      label: "ОРУЖИЕ",
      caption: "ОРУЖИЕ",
      name: weaponLabels[profile.weaponId],
      grade: "I".repeat(profile.equipmentTiers.weapon + 1),
      hotkey: "01",
      theme: "weapon",
      equipment: { kind: "weapon", id: profile.weaponId },
      context: { kind: "weapon" },
    },
    {
      label: "МОДУЛЬ 1 · Q",
      caption: "МОДУЛЬ 1",
      name: moduleLabels[profile.moduleId],
      grade: "I".repeat(profile.equipmentTiers.module + 1),
      hotkey: "Q",
      theme: "primary",
      equipment: { kind: "module", id: profile.moduleId },
      context: { kind: "module", slot: 0 },
    },
    {
      label: "МОДУЛЬ 2 · 2",
      caption: "МОДУЛЬ 2",
      name: moduleLabels[profile.secondaryModuleId],
      grade: "I".repeat(profile.equipmentTiers.secondaryModule + 1),
      hotkey: "2",
      theme: "secondary",
      equipment: { kind: "module", id: profile.secondaryModuleId },
      context: { kind: "module", slot: 1 },
    },
    {
      label: "УСИЛИТЕЛЬ",
      caption: "УСИЛИТЕЛЬ",
      name: passiveInfo(profile.passiveId).name,
      grade:
        profile.passiveId === "none"
          ? null
          : "I".repeat(profile.equipmentTiers.passive + 1),
      hotkey: "04",
      theme: "passive",
      equipment:
        profile.passiveId === "none"
          ? null
          : { kind: "passive", id: profile.passiveId },
      context: { kind: "passive" },
    },
  ];
  if (profile.shipFitting) {
    let activeIndex = 0;
    slots = fittingCells(profile.shipFitting).map((item, index) => ({
      label: `ЯЧЕЙКА ${index + 1}`,
      caption: `ЯЧЕЙКА ${index + 1}`,
      name: item ? equipmentDefinition(item).name : "Свободно",
      grade: item ? "I".repeat(item.tier + 1) : null,
      hotkey: item?.kind === "module" ? abilityHotkey(activeIndex++) : "",
      theme:
        item?.kind === "weapon"
          ? "weapon"
          : item?.kind === "module"
            ? "primary"
            : "passive",
      equipment: item,
      context: { kind: "cell", slot: index },
    }));
  }
  const trainingSlot = profile.tutorial?.version === 2 && profile.tutorial.rewardGranted &&
    !profile.tutorial.milestones.includes("moduleEquipped")
    ? slots.find(slot => !slot.equipment) ?? slots.find(slot => slot.equipment?.kind === "module" && slot.equipment.id === "repair") ??
      slots.find(slot => slot.equipment?.kind === "module") ?? slots.find(slot => slot.equipment?.kind !== "weapon")
    : undefined;
  return (
    <section
      className="equipment-summary equipment-tiles"
      aria-label="Установленное оснащение"
    >
      {slots.map((slot) => (
        <button
          key={slot.label}
          type="button"
          className={`equipment-slot slot-${slot.theme}${slot.grade === null ? " slot-empty" : ""}`}
          data-equipment-id={slot.equipment?.id}
          data-tutorial-highlight={trainingSlot?.label === slot.label || undefined}
          onClick={() => onOpen(slot.context)}
          disabled={busy}
          aria-pressed={
            context ? sameEquipmentContext(context, slot.context) : false
          }
          aria-label={`Изменить: ${slot.label}`}
          title={`${slot.label}: ${slot.name}${slot.grade ? ` MK ${slot.grade}` : ""}`}
        >
          {equipmentBackdrop(slot.equipment) && (
            <img
              className="equipment-slot-background"
              src={equipmentBackdrop(slot.equipment) ?? undefined}
              alt=""
              aria-hidden="true"
              decoding="async"
            />
          )}
          <span className="equipment-slot-art" aria-hidden="true">
            <EquipmentArt equipment={slot.equipment} />
          </span>
          <span className="equipment-slot-label">{slot.caption}</span>
          {slot.hotkey && <kbd className="equipment-slot-key" aria-hidden="true">
            {slot.hotkey}
          </kbd>}
          <b className="equipment-slot-name">{slot.name}</b>
          <span className="equipment-slot-grade">
            {slot.grade === null ? "ВЫБРАТЬ" : `MK ${slot.grade}`}
          </span>
        </button>
      ))}
    </section>
  );
}
